import React, { useState, useCallback } from "react";
import { FaBirthdayCake, FaEdit, FaPlus, FaEllipsisV } from "react-icons/fa";
import { MdMedicalServices } from "react-icons/md";
import { statusColor } from "./utils";

const AdherentCard = ({
  adherent,
  isBirthday,
  getDefaultPhoto,
  onClick,
  isAdmin,
  onEdit,
  onManageGroups,
}) => {
  // Ouvre/ferme le panneau admin sur mobile
  const [adminOpen, setAdminOpen] = useState(false);

  const handleCardClick = useCallback(() => {
    // si le panneau admin est ouvert sur mobile, on ne ferme pas la fiche par mégarde
    // mais on laisse quand même l’ouverture de fiche si souhaité
    onClick?.(adherent);
  }, [adherent, onClick]);

  const toggleAdmin = useCallback((e) => {
    e.stopPropagation(); // ne pas ouvrir la fiche
    setAdminOpen((v) => !v);
  }, []);

    const toAbsolute = (u) => {
    if (!u) return "";
    const s = String(u).trim();
    // déjà absolu, data URL ou blob
    if (/^https?:\/\//i.test(s) || s.startsWith("data:") || s.startsWith("blob:")) return s;
    // chemins d'assets front (ex: /img/...): on laisse tel quel
    if (!s.startsWith("/uploads")) return s;
    // fichiers uploadés côté API
    const base = process.env.REACT_APP_API_URL?.replace(/\/$/, "") || "";
    return `${base}${s}`;
  };

  const photoSrc = adherent?.autorisation_photo
    ? (toAbsolute(adherent?.photo_url) || getDefaultPhoto(adherent?.genre))
    : "/img/interdiction.jpg";

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={handleCardClick}
      onKeyDown={(e) => e.key === "Enter" && handleCardClick()}
      className="relative group rounded-xl overflow-hidden shadow ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white dark:bg-slate-900 cursor-pointer"
    >
      {/* Badges */}
      {isBirthday && (
        <div className="absolute top-2 right-2 z-10">
          <FaBirthdayCake className="text-white bg-primary rounded-full p-2 w-10 h-10 shadow-lg" />
        </div>
      )}
      {adherent?.is_blessed && (
        <div className="absolute top-2 left-2 z-10">
          <MdMedicalServices className="text-white bg-red-600 rounded-full p-2 w-10 h-10 shadow-lg" />
        </div>
      )}

      {/* Bouton mobile pour ouvrir le panneau admin */}
      {isAdmin && (
        <button
          type="button"
          onClick={toggleAdmin}
          title={adminOpen ? "Fermer les actions" : "Ouvrir les actions"}
          className="md:hidden absolute bottom-2 right-2 z-10 rounded-full bg-slate-900/85 text-white w-10 h-10 flex items-center justify-center shadow-lg hover:scale-105 active:scale-95 transition"
        >
          <FaEllipsisV />
        </button>
      )}

      {/* Photo */}
      <figure className="aspect-square">
        <img
          src={photoSrc}
          alt={`${adherent?.nom || ""} ${adherent?.prenom || ""}`}
          className="object-cover w-full h-full"
          loading="lazy"
        />
      </figure>

      {/* Nom + statut */}
      <div className="p-3 text-center">
        <p className="text-sm font-medium">
          {adherent?.nom} {adherent?.prenom}
        </p>
        <div
          className={`mt-1 inline-block text-xs px-2 py-1 rounded ${statusColor(
            adherent?.statut_inscription
          )}`}
        >
          {adherent?.statut_inscription || "—"}
        </div>
      </div>

      {/* Panneau admin : 
         - Desktop : apparaît au hover (md:group-hover:translate-y-0)
         - Mobile : apparaît si adminOpen = true
         - Plus haut/présent : min-height + padding + blur + gradient + ombre supérieure
      */}
      {isAdmin && (
        <div
          onClick={(e) => e.stopPropagation()}
          className={[
            "absolute inset-x-0 bottom-0",
            "bg-gradient-to-r from-blue-500/95 via-violet-500/95 to-pink-500/95",
            "backdrop-blur-sm text-white",
            "px-3 py-2 md:py-3",
            "flex items-center justify-center gap-3",
            "shadow-[0_-10px_22px_-12px_rgba(0,0,0,0.45)]",
            "transition-transform duration-250",
            "min-h-[72px]", // ← monte plus (plus haut et visible)
            // États : caché par défaut, visible au hover desktop, ou via adminOpen mobile
            adminOpen ? "translate-y-0" : "translate-y-full md:group-hover:translate-y-0",
          ].join(" ")}
        >
          {/* petite poignée visible en mobile */}
          <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-12 h-1.5 rounded-full bg-white/70 md:hidden" />

          <button
            className="btn btn-xs md:btn-sm btn-ghost gap-1 !text-white !bg-white/10 hover:!bg-white/20"
            onClick={() => onEdit?.(adherent)}
          >
            <FaEdit /> <span className="hidden sm:inline">Éditer</span>
          </button>

          <button
            className="btn btn-xs md:btn-sm btn-ghost gap-1 !text-white !bg-white/10 hover:!bg-white/20"
            onClick={() => onManageGroups?.(adherent)}
          >
            <FaPlus /> <span className="hidden sm:inline">Gérer groupes</span>
          </button>
        </div>
      )}
    </div>
  );
};

export default AdherentCard;
